/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import type Store from "@ember-data/store";
import { action } from "@ember/object";
import type RouterService from "@ember/routing/router-service";
import { service } from "@ember/service";
import { tracked } from "@glimmer/tracking";
import type { BufferedChangeset } from "ember-changeset/types";
import type { ForcBaseArgs } from "parog-web/components/forc/abstract/base-component";
import ForcBaseComponent from "parog-web/components/forc/abstract/base-component";
import MedicationPrescribedJournalEvent from "parog-web/models/medication-prescribed-journal-event";
import type Patient from "parog-web/models/patient";
import type MedicationRequest from "parog-web/models/prescriptions/medication-request";
import type AnalyticsService from "parog-web/services/analytics";
import styles from "./index.module.css";

interface Period {
  duration: number;
  unit: "day" | "days" | "week" | "weeks" | "month" | "months" | "year";
}

interface Args extends ForcBaseArgs {
  changeset: BufferedChangeset;
  patient: Patient;
  oldMedicationPrescribed: MedicationPrescribedJournalEvent | undefined;
}

export default class PrescriptionMedicationEditionForm extends ForcBaseComponent<Args> {
  styles = styles;

  @service declare router: RouterService;
  @service declare store: Store;
  @service declare analytics: AnalyticsService;

  @tracked declare chatMessage: string;
  @tracked declare willSendMessage: boolean;

  static fullComponentId = "prescription/medication-edition-form";

  static journalEventClass = MedicationPrescribedJournalEvent;

  constructor(owner: unknown, args: Args) {
    super(owner, args);

    this.chatMessage =
      "Bonjour,\nJe viens de vous éditer une nouvelle ordonnance. Elle est jointe à ce message.\nBonne journée.";
    this.willSendMessage = true;
  }

  hasMedication = (medications: unknown, medication: MedicationRequest) =>
    (medications as MedicationRequest[]).some(m => m.isEqual(medication));

  get changeset() {
    return this.args.changeset;
  }

  async save() {
    this.forceQuit = true;
    await this.args.changeset.validate();

    if (this.args.changeset.get("isValid")) {
      await this.args.changeset.save();
      const savedModel = this.args.changeset
        .data as MedicationPrescribedJournalEvent;

      if (
        this.patient.authorizesAppUsage &&
        this.willSendMessage &&
        this.chatMessage.trim()
      ) {
        await this.sendChatMessages();
      }

      this.processRenewAlertTriggered();

      this.router.transitionTo(
        "medics-web.protected.patients.patient.prescriptions.medication-display",
        savedModel.patient.id,
        savedModel.id
      );
    }
  }

  async sendChatMessages() {
    const chatMessageJe = this.store.createRecord(
      "patient-chat-message-sent-journal-event",
      {
        occurredAt: new Date(),
        patient: this.patient,
        body: this.chatMessage
      }
    );
    this.analytics.trackJeCreation(chatMessageJe);
    await chatMessageJe.save();

    await this.store
      .createRecord("patient-chat-message-sent-journal-event", {
        occurredAt: new Date(),
        triggeringJournalEvent: this.args.changeset.data,
        patient: this.patient,
        body: "Nouvelle ordonnance reçue"
      })
      .save();
  }

  processRenewAlertTriggered() {
    const alertsTriggered = this.store.peekAll("alert-triggered-journal-event");
    const alert = alertsTriggered.find(
      alert =>
        alert.triggeringJournalEvent?.id ==
        this.args.oldMedicationPrescribed?.id
    );
    if (alert) {
      alert.processAlert({ actionTaken: "medication-prescription-renewed" });
    }
  }

  get latestTreatmentPrescription() {
    return this.patient.lastTreatmentPrescriptionChangedJournalEvent;
  }

  get wholeCarePlanSelected() {
    return (
      this.changeset.get("medications").length ===
      this.latestTreatmentPrescription?.prescriptions.length
    );
  }

  get hasPps(): boolean {
    return !!this.latestTreatmentPrescription?.prescriptions?.length;
  }

  @action
  updateChatMessage(event) {
    this.chatMessage = event.target.value;
  }

  @action
  toggleCarePlanSelection(checked) {
    if (checked) {
      this.changeset.set(
        "medications",
        this.latestTreatmentPrescription?.prescriptions.slice()
      );
    } else {
      this.changeset.set("medications", []);
    }
  }

  @action
  togglePrescription(prescription: MedicationRequest) {
    let selection: MedicationRequest[] = this.changeset
      .get("medications")
      .slice();

    const finded = selection.find(m => m.isEqual(prescription));
    if (finded) {
      selection = selection.filter(m => !m.isEqual(prescription));
    } else {
      selection.push(prescription);
    }

    this.changeset.set("medications", selection);
  }

  /**
   * Roll backs changeset and do transition.
   * The changeset is rolled back in case the Medic already start completed
   * the form. This ensure no "Are you sure you want to leave the form" alert is
   * shown, and fixes a "too many recursion error" if this alert is displayed.
   */
  @action
  showTreatmentForm() {
    this.changeset.rollback();

    this.router.transitionTo(
      `medics-web.protected.patients.patient.treatment.edition-form`,
      this.patient
    );
  }

  @action
  appendVaccine(changeset, str) {
    const prevVaccins = changeset.get("otherMedicationsAld") || "";
    switch (str) {
      case "Vaccination anti-grippale":
        changeset.set(
          "otherMedicationsAld",
          `${prevVaccins}` +
            "• VACCINATION ANTI-GRIPPALE\n    1 injection IM dès que le vaccin est disponible\n"
        );
        break;
      case "Vaccination anti-pneumocoque (PREVENAR 13, PNEUVOVAX)":
        changeset.set(
          "otherMedicationsAld",
          `${prevVaccins}` +
            "• VACCINATION ANTI-PNEUMOCOQUE\n    PREVENAR 13 : 1 injection IM puis 8 semaines plus tard\n    PNEUMOVAX : 1 injection IM \n"
        );
        break;
      case "Rappel de vaccination anti-pneumocoque (PNEUMOVAX)":
        changeset.set(
          "otherMedicationsAld",
          `${prevVaccins}` +
            "• RAPPEL DE VACCINATION ANTI-PNEUMOCOQUE\n    PNEUMOVAX : 1 injection IM \n"
        );
        break;
      case "Vaccination anti covid":
        changeset.set(
          "otherMedicationsAld",
          `${prevVaccins}` + "• Vaccination anti-COVID en très haute priorité\n"
        );
        break;
    }
  }

  get suggestionComments() {
    return [
      "Vaccination anti-grippale",
      "Vaccination anti-pneumocoque (PREVENAR 13, PNEUVOVAX)",
      "Rappel de vaccination anti-pneumocoque (PNEUMOVAX)",
      "Vaccination anti covid"
    ];
  }

  get validityPeriod() {
    const periodArray: Period[] = [];

    periodArray.push({ duration: 1, unit: "day" });
    for (let i = 2; i <= 13; i++) {
      periodArray.push({
        duration: i,
        unit: "days"
      });
    }
    periodArray.push(
      { duration: 1, unit: "week" },
      { duration: 2, unit: "weeks" },
      { duration: 3, unit: "weeks" }
    );
    periodArray.push({ duration: 1, unit: "month" });
    for (let i = 2; i <= 6; i++) {
      periodArray.push({
        duration: i,
        unit: "months"
      });
    }
    periodArray.push({ duration: 1, unit: "year" });
    return periodArray;
  }
}
